import React, { useState, useRef, useCallback } from 'react';
import AppLoader from './AppLoader/AppLoader';
import './ImportReportModal.css';

function ImportReportModal({ isOpen, onClose, onImport }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const processFile = useCallback(async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      setError('Please select a JSON file');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const text = await file.text();
      const reportJson = JSON.parse(text);
      
      // Validate it looks like a report
      if (!reportJson.deck_id && !reportJson.data_items) {
        setError('This does not appear to be a valid parsing report');
        setIsLoading(false);
        return;
      }

      await onImport(reportJson);
      onClose();
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON file');
      } else {
        setError(err.message || 'Failed to import report');
      }
    } finally {
      setIsLoading(false);
    }
  }, [onImport, onClose]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  }, [processFile]);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [processFile]);

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="import-modal-overlay" onClick={handleOverlayClick}>
      <div className="import-modal">
        <div className="import-modal-header">
          <h3>Import Report</h3>
          <button 
            className="import-modal-close" 
            onClick={onClose}
            disabled={isLoading}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div 
          className={`import-drop-zone ${isDragging ? 'dragging' : ''} ${isLoading ? 'loading' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isLoading ? (
            <div className="app-loader-shell app-loader-shell--compact">
              <AppLoader label="Importing report..." size="sm" />
            </div>
          ) : (
            <>
              <svg className="import-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M7 18a4.6 4.4 0 0 1 0-9 5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-1" />
                <path d="M12 13v9" />
                <path d="M9 16l3-3 3 3" />
              </svg>
              <p className="import-text">
                Drag & drop your report JSON here
              </p>
              <p className="import-subtext">or</p>
              <button 
                className="import-browse-btn"
                onClick={handleBrowseClick}
              >
                Browse Files
              </button>
            </>
          )}
        </div>

        {error && (
          <div className="import-error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        <p className="import-hint">
          Reports are generated after parsing a PowerPoint in admin mode.
          Find them in <code>data/results/</code>
        </p>
      </div>
    </div>
  );
}

export default ImportReportModal;
